'use client';

import { canCustomizeAvatar, newAvatarSeed, REMOVE_AVATAR } from '@shared/all/domains/avatar';
import { type PublicUser } from '@shared/all/domains/user';
import toast from '@shared/utils/helpers/toast';
import { getDimensionsFromUri } from '@shared/utils/helpers/uploadHelper';
import usePinnedFactiiis from '@shared/utils/hooks/usePinnedFactiiis';
import useUpload from '@shared/utils/hooks/useUpload';
import useUser from '@shared/utils/hooks/useUser';
import { Camera, Palette, Pencil, RefreshCw } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { getS3ObjectURL } from '../../utils/s3';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Upload from '../common/Upload';
import UserFactiiisList from '../factiiis/UserFactiiisList';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
import AvatarStyleEditor from './AvatarStyleEditor';
import VerifiedAvatarChooser from './VerifiedAvatarChooser';
import VerifiedBioChooser from './VerifiedBioChooser';

/**
 * The editable profile avatar and the diameter of the control on its rim.
 *
 * Named because Avatar needs both to place the control: the geometry is a
 * function of the avatar size and the control's own size, so a literal in one
 * place and not the other is how the two drift.
 */
const AVATAR_SIZE = 100;
const AVATAR_MENU_SIZE = 32;
enum UserUploadType {
  HEADER = 'Header',
  PROFILE = 'Profile',
}
type Props = {
  closeHandler: () => void;
  initialUserData: NonNullable<PublicUser>;
  refetch: () => void;
};
const ProfileEdit = (props: Props) => {
  const {
    currentUser,
    setUser
  } = useUser();
  const {
    upload,
    uploadOne,
    addFile
  } = useUpload(client);
  const {
    pinnedFactiiis
  } = usePinnedFactiiis(client);
  const [userData, setUserData] = useState(props.initialUserData);
  const [buttonLoading, setButtonLoading] = useState(false);
  /**
   * Which secondary modal is open, as one value rather than one flag each.
   *
   * They are mutually exclusive — every one of them is a full-screen modal over
   * this one — so a flag apiece could express states that cannot happen, and
   * this screen was already over the five-useState line before the avatar
   * editor arrived.
   *
   * `avatarUpload` and `coverUpload` carry the aspect and the title the Upload
   * component needs; `approvedAvatar` is the pre-approved chooser a VERIFIED
   * account gets instead of a free upload (bytes from local storage; on save
   * they go through the standard upload pipeline and updateUser hashes them
   * against LocalDataHash for the enforcement check).
   */
  const [openModal, setOpenModal] = useState<null | 'avatarUpload' | 'coverUpload' | 'approvedAvatar' | 'approvedBio' | 'avatarStyle' | 'factiiis'>(null);
  const [pendingApprovedAvatar, setPendingApprovedAvatar] = useState<{
    id: string;
    bytes: string;
  } | null>(null);
  const isVerified = currentUser.tag === 'VERIFIED';
  // Only a verified human may store style choices; the server enforces the same
  // rule in users.updateUser. See canCustomizeAvatar for why the tag stands in
  // for verifiedHumanAt on the client.
  const canCustomize = canCustomizeAvatar(currentUser.tag);

  // Detect if any changes have been made (compare state to initial values)
  const hasChanges = userData !== props.initialUserData || !!pendingApprovedAvatar;
  const saveHandler = async () => {
    setButtonLoading(true);
    if (userData.name && userData.name.length < 2) {
      toast('Your name cannot be less than two characters', '', 'error');
      setButtonLoading(false);
      return;
    }
    const avatarChanged = userData.avatarKey !== props.initialUserData.avatarKey;
    const bannerChanged = userData.bannerKey !== props.initialUserData.bannerKey;

    // Clearing a picture is a change with nothing to upload — the generated
    // avatar takes over. Telling the two apart is the whole fix: the guard
    // below read "changed but nothing uploaded" as a failed upload and returned
    // before saving, so switching back to a generated avatar silently did
    // nothing and the profile kept the old picture.
    const avatarRemoved = avatarChanged && !userData.avatarKey;
    const bannerRemoved = bannerChanged && !userData.bannerKey;

    // Verified-chooser path: shove the canonical bytes through the standard upload
    // pipeline (composeUpload preserves the raw blob for non-HEIC inputs, so the bytes
    // server-side stay byte-stable for the hash check in updateUser enforcement).
    let chooserAvatarId: string | undefined;
    if (pendingApprovedAvatar) {
      const binary = atob(pendingApprovedAvatar.bytes);
      const arr = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
      const blob = new Blob([arr], {
        type: 'image/webp'
      });
      const blobUrl = URL.createObjectURL(blob);
      try {
        const result = await uploadOne(blobUrl, 'verified-avatar.webp', {
          width: 512,
          height: 512
        });
        if (!result) {
          setButtonLoading(false);
          return;
        }
        chooserAvatarId = result.id;
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    }
    const results = pendingApprovedAvatar ? {} : await upload();
    // Only bail when an upload was actually expected and produced nothing.
    const expectsUpload = avatarChanged && !avatarRemoved || bannerChanged && !bannerRemoved;
    if (!pendingApprovedAvatar && expectsUpload && Object.keys(results).length === 0) {
      setButtonLoading(false);
      return;
    }
    const avatarId = chooserAvatarId ?? results['avatar']?.id;
    const bannerId = results['banner']?.id;

    // `undefined` means "leave it alone", so removal needs its own value —
    // see REMOVE_AVATAR. Written out rather than nested ternaries.
    const resolveAvatarId = (): string | undefined => {
      if (chooserAvatarId) return chooserAvatarId;
      if (avatarRemoved) return REMOVE_AVATAR;
      if (avatarChanged) return avatarId;
      return undefined;
    };
    const [error, updatedProfile] = await client.users.updateUser.mutate({
      name: userData.name || null,
      bio: userData.bio || null,
      bannerId: bannerChanged ? bannerId : undefined,
      avatarId: resolveAvatarId(),
      robohash: userData.robohash !== null ? userData.robohash : undefined
    });
    if (error) {
      toast(error.message, '', 'error');
      setButtonLoading(false);
      return;
    }
    if (updatedProfile) {
      // Update currentUser in global state synchronously before refetch
      flushSync(() => {
        setUser({
          ...currentUser,
          name: updatedProfile.name,
          bio: updatedProfile.bio,
          avatarKey: updatedProfile.avatarKey,
          bannerKey: updatedProfile.bannerKey,
          robohash: updatedProfile.robohash
        });
      });
      // Trigger refetch to sync query cache, then close modal
      props.refetch();
      toast('Profile updated successfully. Great job!', '', 'success');
      props.closeHandler();
    }
    setButtonLoading(false);
  };
  const uploadCallbackFunction = (type: UserUploadType) => {
    return async (file: File) => {
      const key = type === UserUploadType.PROFILE ? 'avatar' : 'banner';
      const previewUrl = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(previewUrl);
      addFile(key, {
        uri: previewUrl,
        fileName: file.name,
        width: dimensions?.width,
        height: dimensions?.height
      });
      switch (type) {
        case UserUploadType.PROFILE:
          setUserData(prev_0 => ({
            ...prev_0,
            avatarKey: previewUrl
          }));
          break;
        case UserUploadType.HEADER:
          setUserData(prev => ({
            ...prev,
            bannerKey: previewUrl
          }));
          break;
      }
      setOpenModal(null);
    };
  };

  // A fresh seed redraws the generated avatar. Open to everyone: it has always
  // been, the brand backdrop does not depend on the seed, and only the style
  // *choices* are what human verification unlocks. Any choices already made are
  // dropped with the old seed — this button is the "give me a different one"
  // control, not a partial shuffle.
  const getNewAvatarSeed = () => {
    setUserData({
      ...userData,
      robohash: newAvatarSeed(),
      avatarKey: null
    });
  };

  /**
   * The camera control and the actions it fans out into.
   *
   * Handed to Avatar as its `action` rather than positioned here. Avatar places
   * it with the same 45° rim geometry the badges use, so the control's centre
   * lands on the avatar circle at any size — this used to be `bottom-2 right-0`
   * plus per-button `translate-x`/`translate-y` pairs that were tuned for one
   * avatar size and drifted at every other. Avatar also hides the status badges
   * while a control is present, so the online dot no longer sits under the
   * button the user is aiming at.
   */
  /**
   * The camera that sits on the avatar's rim: upload a picture, or pick an
   * approved one if this account is verified. Handed to Avatar as its `action`
   * so the 45° rim geometry places it — see getAvatarCornerOffsets.
   *
   * One button, one job. It used to open a fan-out whose first entry was
   * another camera, so reaching "upload" meant clicking a camera to find a
   * camera.
   */
  const avatarCamera = <Button variant="CLEAR" onClick={() => setOpenModal(isVerified ? 'approvedAvatar' : 'avatarUpload')} icon={<Camera height={15} width={15} />} tooltip={isVerified ? 'Choose Approved Avatar' : 'Upload a picture'} />;
  const BannerComponent = <>
      <div className="relative">
        <Button text="Edit Cover Photo" variant="CLEAR" classNames="absolute top-2 right-2 w-auto" onClick={() => setOpenModal('coverUpload')} titleElement={<div className="flex items-center gap-x-2 text-sm font-semibold">
              <Camera height={15} width={15} />
              <p>Edit Cover Photo</p>
            </div>} />
        <Image height={0} width={0} sizes="100vw" style={{
        width: '100%',
        height: 'auto',
        maxHeight: '200px',
        objectFit: 'cover'
      }} src={userData.bannerKey ? getS3ObjectURL(userData.bannerKey) : '/images/header-image-default.jpg'} unoptimized={!!userData.bannerKey?.startsWith('blob:')} alt="profile banner" onError={() => setUserData(prev_1 => ({
        ...prev_1,
        bannerKey: null
      }))} />
      </div>
      <div className="flex">
        <div className="relative">
          <Avatar avatarKey={userData.avatarKey} robohash={userData.robohash} isBot={userData.tag === 'BOT'} name={userData.name} username={userData.username} size={AVATAR_SIZE} className="-mt-14 ml-4 border-4 border-background-secondary" userId={currentUser.id} action={{
          size: AVATAR_MENU_SIZE,
          node: avatarCamera
        }} />
        </div>
        {/* Beside the avatar rather than on it: the rim holds one control, and
            a second there would collide with the camera at every avatar size.
            Labelled because "give me a different generated avatar" is not
            something an icon says on its own. */}
        <div className="flex items-end gap-x-2 pb-2 pl-2">
          <Button variant="CLEAR" classNames="w-auto" onClick={getNewAvatarSeed} titleElement={<div className="flex items-center gap-x-2 text-sm font-semibold">
                <RefreshCw height={15} width={15} />
                <p>New Avatar</p>
              </div>} tooltip="Draw a different generated avatar" />
          {canCustomize && <Button variant="CLEAR" classNames="w-auto" onClick={() => setOpenModal('avatarStyle')} titleElement={<div className="flex items-center gap-x-2 text-sm font-semibold">
                  <Palette height={15} width={15} />
                  <p>Customise</p>
                </div>} tooltip="Choose how your avatar looks" />}
        </div>
      </div>
    </>;
  return <>
      <Modal closeHandler={props.closeHandler}>
        <ModalHeader closeHandler={props.closeHandler}>
          <h2>Edit Profile</h2>
        </ModalHeader>
        <ModalContent banner={BannerComponent}>
          <div className="mt-6 w-full space-y-4 px-3 pb-3">
            <Input type="text" label="Name" value={userData.name ?? ''} onChange={value => setUserData({
            ...userData,
            name: value ? value : null
          })} />
            <div className="relative">
              <Input type="textarea" label="Bio" value={userData.bio ?? ''} onChange={value_0 => setUserData({
              ...userData,
              bio: value_0 ? value_0 : null
            })} />
              {isVerified && <button type="button" onClick={() => setOpenModal('approvedBio')} className="absolute right-3 top-8 rounded p-1.5 text-textSecondary transition hover:bg-background-light hover:text-textPrimary" title="Choose approved bio">
                  <Pencil size={16} />
                </button>}
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <div className="flex w-full items-center justify-between">
            <Button text="Close" variant="SECONDARY" onClick={props.closeHandler} />
            <div className="flex gap-2">
              {hasChanges && <Button text="Cancel" variant="SECONDARY" onClick={() => setUserData(props.initialUserData)} />}
              <Button text="Save" onClick={saveHandler} loading={buttonLoading} disabled={buttonLoading || !hasChanges} />
            </div>
          </div>
        </ModalFooter>
        {(openModal === 'avatarUpload' || openModal === 'coverUpload') && <Upload crop={{
        enabled: true,
        aspect: openModal === 'coverUpload' ? 3 : 1,
        circularCrop: openModal === 'avatarUpload'
      }} title={openModal === 'avatarUpload' ? 'Upload Profile Avatar' : 'Upload Cover Photo'} closeHandler={() => setOpenModal(null)} callbackFn={uploadCallbackFunction(openModal === 'avatarUpload' ? UserUploadType.PROFILE : UserUploadType.HEADER)} enableCamera />}
        {openModal === 'avatarStyle' && <AvatarStyleEditor spec={userData.robohash} closeHandler={() => setOpenModal(null)} onSave={spec => setUserData(prev_2 => ({
        ...prev_2,
        robohash: spec,
        avatarKey: null
      }))} />}
        {openModal === 'approvedAvatar' && <VerifiedAvatarChooser closeHandler={() => setOpenModal(null)} onSelect={avatar => {
        setPendingApprovedAvatar(avatar);
        // Preview is a data URL from the bytes we already have client-side.
        setUserData(prev_3 => ({
          ...prev_3,
          avatarKey: `data:image/webp;base64,${avatar.bytes}`
        }));
      }} />}
        {openModal === 'approvedBio' && <VerifiedBioChooser closeHandler={() => setOpenModal(null)} onSelect={bio => setUserData(prev_4 => ({
        ...prev_4,
        bio
      }))} />}
        {/* Nothing sets 'factiiis' yet — the list has been rendered here since
            before this file used one openModal value, with no control wired to
            open it. Kept as-is rather than deleted; consolidating the modal
            flags is what made the gap visible, not what caused it. */}
        {openModal === 'factiiis' && <Modal closeHandler={() => setOpenModal(null)}>
            <ModalHeader>
              <h2>Your factiiis</h2>
            </ModalHeader>
            <UserFactiiisList pinnedFactiiis={pinnedFactiiis.map(f => ({
          ...f,
          spaceSlug: f.spaceSlug ?? ''
        }))} username={currentUser.username} />
          </Modal>}
      </Modal>
    </>;
};
export default ProfileEdit;